import { TextField, Tooltip } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@mui/icons-material/Add';
import { useAppFlags } from 'utils/useAppFlags';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { BreadCrumbData } from '@redux/common/types';
import { getDevicePersonOverviewThunk } from '@redux/device/deviceThunks';
import { appendBreadcrumbAction } from '@redux/oauth/oauthActions';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { clearPersonVitalsAction } from '@redux/person/personActions';
import {
  getPersonByIdSelector,
  getPersonVitalsByPersonIdSelector,
  getDevicePersonByPersonIdSelector,
  hasPatientCreatePermissionSelector,
  getNextPageTokenSelector,
  getPersonLoadingStateSelector
} from '@redux/person/personSelector';
import { getPersonByIdThunk, getPersonVitalsThunk, refreshPersonVitalsThunk, setSelectedPatientIdThunk } from '@redux/person/personThunks';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import DeletePatientModal from 'components/DeletePatientModal/DeletePatientModal';
import NavTopBarContentWrapper from 'components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { PatientInfoDrawer } from 'components/PatientInfoDrawer/PatientInfoDrawer';
import style from './AllPatients.scss';
import { CustomizeTableMenu } from './components/CustomizeTableMenu/CustomizeTableMenu';
import { useCustomizeColumnsConfig } from './components/CustomizeTableMenu/useCustomizeColumnsConfig';
import NoPatients from './components/NoPatients';
import PatientTable, { DEFAULT_SORT_CONFIG } from './components/PatientTable/PatientTable';
import { PatientTableData, PATIENTS_SORT_STORAGE_KEY } from './components/PatientTable/PatientTableTypes';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;

type Props = PropsFromRedux & {};

const AllPatients: React.FC<Props> = ({
  hasPatientCreatePermission,
  personVitalsByPersonIdSelector,
  authPersonSelector,
  personByIdSelector,
  devicePersonByPersonIdSelector,
  nextPageTokenSelector,
  isLoadingSelector,
  activeOrganizationId,
  getPersonVitals,
  clearPersonVitals,
  setSelectedPatientIdThunkCallback,
  fetchPersonById,
  fetchDevicePersonOverview,
  refreshPersonVitals
}) => {
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [deletingPatientId, setDeletingPatientId] = useState<string>('');
  const organizationId = activeOrganizationId;
  const [showNoPatients, setShowNoPatients] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [hasSearchStarted, setHasSearchStarted] = useState<boolean>(false);
  const noPatientsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDtcOrganizationCached, setIsDtcOrganizationCached] = useState<boolean | null>(null);

  const patientTableData = useMemo(() => {
    const tableData: PatientTableData[] = [];
    for (const personId of Object.keys(personVitalsByPersonIdSelector)) {
      const personVitals = personVitalsByPersonIdSelector[personId];
      const devicePerson = devicePersonByPersonIdSelector[personId];
      if (!personVitals) {
        continue;
      }

      const tableRow: PatientTableData = {
        ...personVitals,
        name: personVitals.personName ?? '',
        avatar: '',
        deviceNumber: devicePerson?.deviceNumber ?? undefined
      };
      tableData.push(tableRow);
    }

    return tableData;
  }, [personVitalsByPersonIdSelector, devicePersonByPersonIdSelector]);

  const flags = useAppFlags();

  const noPatientsCondition = !isLoadingSelector['person-redux/refresh-person-vitals'] && !isLoadingSelector['person-redux/get-person-vitals'] && patientTableData.length === 0;

  const [sortBy, setSortBy] = useState<string>(() => {
    const saved = localStorage.getItem(PATIENTS_SORT_STORAGE_KEY) ? JSON.parse(localStorage.getItem(PATIENTS_SORT_STORAGE_KEY) ?? '') : DEFAULT_SORT_CONFIG;
    return saved.key + '_' + saved.direction;
  });

  const navigate = useNavigate();
  const { columnsConfig, toggleColumn } = useCustomizeColumnsConfig(authPersonSelector?.id, flags);

  useEffect(() => {
    fetchDevicePersonOverview();
    refreshPersonVitals(organizationId, sortBy);
  }, [organizationId, sortBy]);

  const unselectPatientId = useCallback(() => {
    setSelectedPatientId('');
  }, []);

  const onDeletePatientClick = (personId: string) => {
    setDeletingPatientId(personId);
  };

  const searchPatients = async (searchText: string) => {
    if (isLoadingSelector['person-redux/get-person-vitals']) {
      return;
    }
    const trimmedSearch = searchText.trim();
    if (trimmedSearch.length === 0 || trimmedSearch.length >= 3) {
      clearPersonVitals();
      await getPersonVitals(organizationId, undefined, searchText, sortBy);
    }
  };

  const searchPatientsRef = useRef(searchPatients);

  useEffect(() => {
    searchPatientsRef.current = searchPatients;
  }, [searchPatients]);

  useEffect(() => {
    if (hasSearchStarted) {
      searchPatientsRef.current?.(searchText);
    }
  }, [hasSearchStarted]);

  const onSearchPatients = useCallback(
    debounce((searchText: string) => {
      searchPatientsRef.current?.(searchText);
    }, 500),
    []
  );

  // Delay showing the no patients object to prevent flickering when the reducer sets isLoadingSelector
  // to false before we pull the data from personVitalsByPersonIdSelector
  useEffect(() => {
    if (noPatientsCondition) {
      noPatientsTimeoutRef.current = setTimeout(() => {
        setShowNoPatients(true);
      }, 100);
    } else {
      if (noPatientsTimeoutRef.current) {
        clearTimeout(noPatientsTimeoutRef.current);
        noPatientsTimeoutRef.current = null;
      }
      setShowNoPatients(false);
    }
    return () => {
      if (noPatientsTimeoutRef.current) {
        clearTimeout(noPatientsTimeoutRef.current);
      }
    };
  }, [noPatientsCondition]);

  // Clear cached DTC status when organization changes
  useEffect(() => {
    setIsDtcOrganizationCached(null);
  }, [organizationId]);

  // Cache DTC organization status on initial load (before search filters data)
  useEffect(() => {
    if (isDtcOrganizationCached === null && Object.keys(personVitalsByPersonIdSelector).length > 0) {
      const hasDtcPatient = Object.values(personVitalsByPersonIdSelector).some((personVitals) => personVitals.patientType === 'DTC');
      setIsDtcOrganizationCached(hasDtcPatient);
    }
  }, [personVitalsByPersonIdSelector, isDtcOrganizationCached]);

  const goToPatientDashboard = useCallback((personId: string) => {
    fetchPersonById(personId);
    setSelectedPatientId(personId);
    setSelectedPatientIdThunkCallback(personId);
  }, []);

  const goToNewPatientPage = useCallback(() => {
    navigate('/patients/newPatient');
  }, [navigate]);

  // Use cached value if available, otherwise default to false
  const isAddPatientDisabled = isDtcOrganizationCached ?? false;

  return (
    <NavTopBarContentWrapper
      headerText="Patients"
      contentClassName={style.contentWrapper}
      headerContent={
        hasPatientCreatePermission && patientTableData?.length ? (
          <Tooltip title={isAddPatientDisabled ? 'Adding patients is disabled for DTC organizations' : ''} arrow>
            <span>
              <Button variant="contained" onClick={goToNewPatientPage} startIcon={<AddIcon />} disabled={isAddPatientDisabled}>
                Add Patient
              </Button>
            </span>
          </Tooltip>
        ) : null
      }
    >
      <div className={style.searchContainer}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          className={style.searchField}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value);
            if (value.trim().length === 0) {
              // cancel pending debounced searches and fetch full list
              if (noPatientsTimeoutRef.current) {
                clearTimeout(noPatientsTimeoutRef.current);
              }
              setHasSearchStarted(false);
              onSearchPatients.cancel?.();
              searchPatientsRef.current?.(value);
            } else {
              if (value.trim().length >= 3) {
                if (noPatientsTimeoutRef.current) {
                  clearTimeout(noPatientsTimeoutRef.current);
                }
                noPatientsTimeoutRef.current = setTimeout(() => {
                  setHasSearchStarted(true);
                }, 500);
                onSearchPatients(value);
              }
            }
          }}
          size="small"
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" color="disabled" className={style.searchIcon} />
          }}
        />
        <CustomizeTableMenu toggleColumn={toggleColumn} columnsConfig={columnsConfig} />
      </div>
      {!!patientTableData.length && (
        <PatientTable
          patientTableData={patientTableData}
          columnsConfig={columnsConfig}
          onPressTableRow={goToPatientDashboard}
          onDeletePatientClick={onDeletePatientClick}
          onSortPatients={(sortKey: string) => {
            setSortBy(sortKey);
          }}
          hasPatientCreatePermission={hasPatientCreatePermission}
        />
      )}
      {showNoPatients && <NoPatients onAddPress={goToNewPatientPage} isButtonVisible={hasPatientCreatePermission && !isAddPatientDisabled} isSearching={hasSearchStarted} />}
      <PatientInfoDrawer
        person={personByIdSelector[selectedPatientId]}
        visible={!!selectedPatientId}
        onClose={unselectPatientId}
        onDeletePatientClick={onDeletePatientClick}
        withButtons
        hasPatientCreatePermission={hasPatientCreatePermission}
      />
      <DeletePatientModal patientId={deletingPatientId} visible={!!deletingPatientId} onClosePress={() => setDeletingPatientId('')} onConfirmPress={unselectPatientId} />
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    hasPatientCreatePermission: hasPatientCreatePermissionSelector(state),
    authPersonSelector: getAuthPersonSelector(state),
    personVitalsByPersonIdSelector: getPersonVitalsByPersonIdSelector(state),
    personByIdSelector: getPersonByIdSelector(state),
    devicePersonByPersonIdSelector: getDevicePersonByPersonIdSelector(state),
    nextPageTokenSelector: getNextPageTokenSelector(state),
    isLoadingSelector: getPersonLoadingStateSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state)
  }),
  {
    appendBreadCrumbs: (breadCrumbData: BreadCrumbData, defaultCrumbHistory: BreadCrumbData[], isOldApp?: boolean) =>
      appendBreadcrumbAction({ breadCrumbData, defaultCrumbHistory, isOldApp }),
    getPersonVitals: getPersonVitalsThunk,
    clearPersonVitals: clearPersonVitalsAction,
    setSelectedPatientIdThunkCallback: setSelectedPatientIdThunk,
    fetchPersonById: getPersonByIdThunk,
    fetchDevicePersonOverview: getDevicePersonOverviewThunk,
    refreshPersonVitals: refreshPersonVitalsThunk
  }
);

export { AllPatients as UnconnectedAllPatients };
export default compose(connectRedux)(AllPatients);
